<template>
  <transition name="instructor-profile-fade" appear>
    <div
      class="instructor-profile-overlay"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="`user-instructor-profile-${instructor.id}`"
      @click.self="$emit('close')"
    >
      <div class="instructor-profile-dialog">
        <button
          class="instructor-profile-close"
          type="button"
          :aria-label="$t('general.close')"
          @click="$emit('close')"
        >
          &times;
        </button>

        <div class="instructor-profile-scroll">
          <div class="instructor-profile-layout">
            <aside class="instructor-profile-sidebar">
              <div class="instructor-profile-card">
                <div class="instructor-profile-cover"></div>
                <img
                  :src="instructor.photo"
                  :alt="instructor.name"
                  class="instructor-profile-photo"
                  @error="useFallbackPhoto"
                >
                <div class="instructor-profile-card-body">
                  <span class="instructor-profile-eyebrow">{{ $t("general.instructor") }}</span>
                  <h2 :id="`user-instructor-profile-${instructor.id}`">{{ instructor.name }}</h2>
                  <p class="instructor-profile-title">
                    {{ instructor.title || $t("general.title_not_specified") }}
                  </p>
                  <span class="instructor-profile-type">{{ instructorTypeLabel }}</span>

                  <dl class="instructor-profile-meta">
                    <div>
                      <dt>{{ $t("general.status") }}</dt>
                      <dd :class="`status-${statusClass}`">{{ statusLabel }}</dd>
                    </div>
                    <div>
                      <dt>{{ $t("general.department") }}</dt>
                      <dd>{{ instructor.department || emptyValue }}</dd>
                    </div>
                    <div>
                      <dt>{{ $t("general.institution") }}</dt>
                      <dd>{{ instructor.institutionName || emptyValue }}</dd>
                    </div>
                    <div>
                      <dt>{{ $t("general.created_at") }}</dt>
                      <dd>{{ formattedCreatedAt }}</dd>
                    </div>
                  </dl>

                  <a
                    v-if="instructor.linkedinUrl"
                    class="instructor-profile-linkedin"
                    :href="instructor.linkedinUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i class="uil-linkedin"></i>
                    {{ $t("general.linkedin_profile") }}
                  </a>
                </div>
              </div>
            </aside>

            <main class="instructor-profile-body">
              <div class="instructor-profile-stats">
                <div class="instructor-profile-stat">
                  <i class="uil-clipboard-alt"></i>
                  <div>
                    <strong>{{ assignmentsCount }}</strong>
                    <span>{{ $t("general.assignments") }}</span>
                  </div>
                </div>
                <div class="instructor-profile-stat">
                  <i class="uil-book-open"></i>
                  <div>
                    <strong>{{ contentsCount }}</strong>
                    <span>{{ $t("general.contents") }}</span>
                  </div>
                </div>
                <div class="instructor-profile-stat">
                  <i class="uil-award"></i>
                  <div>
                    <strong>{{ instructor.experienceYears || emptyValue }}</strong>
                    <span>{{ $t("general.experience") }}</span>
                  </div>
                </div>
              </div>

              <section class="instructor-profile-section instructor-profile-section--about">
                <h3>{{ $t("general.about") }}</h3>
                <p>
                  {{ instructor.bio || $t("general.instructor_information_not_found") }}
                </p>
              </section>

              <div class="instructor-profile-grid">
                <section class="instructor-profile-section">
                  <h3>{{ $t("general.expertise_areas") }}</h3>
                  <div v-if="instructor.expertiseAreas.length" class="instructor-profile-tags">
                    <span v-for="expertise in instructor.expertiseAreas" :key="expertise">
                      {{ expertise }}
                    </span>
                  </div>
                  <p v-else class="instructor-profile-empty">
                    {{ $t("general.expertise_area_not_specified") }}
                  </p>
                </section>

                <section class="instructor-profile-section">
                  <h3>{{ $t("general.educations_and_certificates") }}</h3>
                  <ul
                    v-if="instructor.educationsCertificates.length"
                    class="instructor-profile-certificates"
                  >
                    <li
                      v-for="(certificate, index) in instructor.educationsCertificates"
                      :key="`user-instructor-certificate-${index}`"
                    >
                      <i class="uil-award"></i>
                      <span>{{ certificate }}</span>
                    </li>
                  </ul>
                  <p v-else class="instructor-profile-empty">{{ emptyValue }}</p>
                </section>
              </div>

              <section class="instructor-profile-section">
                <h3>{{ $t("general.instructor_detail") }}</h3>
                <div class="instructor-profile-information">
                  <div v-if="!shouldHideEmailField">
                    <span>{{ $t("general.email") }}</span>
                    <strong>{{ email || emptyValue }}</strong>
                  </div>
                  <div>
                    <span>{{ $t("users.mission") }}</span>
                    <strong>{{ position || emptyValue }}</strong>
                  </div>
                  <div>
                    <span>{{ $t("general.instructor_type") }}</span>
                    <strong>{{ instructorTypeLabel }}</strong>
                  </div>
                </div>
              </section>
            </main>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import noPhoto from "@/assets/images/no-photo.png";

export default {
  name: "InstructorProfileModal",
  props: {
    instructor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      emptyValue: "—",
    };
  },
  computed: {
    rawInstructor() {
      return this.instructor.raw || {};
    },
    rawUser() {
      return this.instructor.user?.raw
        || this.rawInstructor.user
        || {};
    },
    assignmentsCount() {
      return Number(
        this.instructor.assignmentsCount
        ?? this.rawInstructor.assignments_count
      ) || 0;
    },
    contentsCount() {
      return Number(
        this.instructor.contentsCount
        ?? this.rawInstructor.contents_count
      ) || 0;
    },
    email() {
      return this.instructor.user?.email
        || this.rawUser.email
        || this.rawUser.mail
        || "";
    },
    shouldHideEmailField() {
      const companyId = this.rawUser.company_id
        || this.rawUser.company?.id
        || this.rawInstructor.company_id;
      return Number(companyId) === 489;
    },
    position() {
      return this.instructor.user?.position
        || this.rawUser.position_name
        || this.instructor.title
        || "";
    },
    instructorTypeLabel() {
      return this.instructor.type === "external"
        ? this.$t("general.external_instructor")
        : this.$t("general.internal_instructor");
    },
    statusLabel() {
      return Number(this.instructor.statusId) === 0
        ? this.$t("general.passive")
        : this.$t("general.active");
    },
    statusClass() {
      return Number(this.instructor.statusId) === 0 ? "passive" : "active";
    },
    formattedCreatedAt() {
      if (!this.instructor.createdAt) return this.emptyValue;
      return new Intl.DateTimeFormat("tr-TR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(new Date(this.instructor.createdAt));
    },
  },
  mounted() {
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", this.handleKeydown);
  },
  beforeDestroy() {
    document.body.style.overflow = "";
    document.removeEventListener("keydown", this.handleKeydown);
  },
  methods: {
    handleKeydown(event) {
      if (event.key === "Escape") this.$emit("close");
    },
    useFallbackPhoto(event) {
      if (event.target.dataset.fallbackApplied) return;
      event.target.dataset.fallbackApplied = "true";
      event.target.src = noPhoto;
    },
  },
};
</script>

<style scoped>
.instructor-profile-overlay {
  position: fixed;
  inset: 0;
  z-index: 11000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(22, 30, 50, 0.66);
}

.instructor-profile-dialog {
  position: relative;
  width: min(980px, 100%);
  max-height: calc(100vh - 48px);
  overflow: hidden;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 24px 70px rgba(17, 29, 58, 0.28);
}

.instructor-profile-close {
  position: absolute;
  top: 18px;
  right: 20px;
  z-index: 3;
  width: 38px;
  height: 38px;
  padding: 0;
  border: 1px solid #dce3ed;
  border-radius: 50%;
  color: #64748b;
  background: #fff;
  font-size: 25px;
  line-height: 32px;
  cursor: pointer;
}

.instructor-profile-scroll {
  max-height: calc(100vh - 60px);
  margin: 6px;
  padding: 26px;
  overflow-y: auto;
  scrollbar-gutter: stable;
  background: #f5f7fa;
}

.instructor-profile-layout {
  display: grid;
  grid-template-columns: minmax(240px, 290px) minmax(0, 1fr);
  gap: 22px;
  padding-top: 26px;
}

.instructor-profile-sidebar {
  min-width: 0;
}

.instructor-profile-card {
  position: sticky;
  top: 0;
  overflow: hidden;
  border: 1px solid #e1e6ee;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 6px 20px rgba(31, 43, 68, 0.06);
}

.instructor-profile-cover {
  height: 92px;
  background:
    radial-gradient(circle at 80% 20%, rgba(115, 201, 45, 0.7), transparent 28%),
    linear-gradient(135deg, #24206d, #5646bf 65%, #302b82);
}

.instructor-profile-photo {
  display: block;
  width: 122px;
  height: 122px;
  margin: -61px auto 0;
  border: 5px solid #fff;
  border-radius: 50%;
  object-fit: cover;
  object-position: center top;
  background: #eef1f5;
}

.instructor-profile-card-body {
  padding: 14px 20px 22px;
  text-align: center;
}

.instructor-profile-eyebrow {
  display: block;
  color: #8792a5;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.instructor-profile-card-body h2 {
  margin: 6px 0 2px;
  color: #172033;
  font-size: 21px;
}

.instructor-profile-title {
  margin: 0 0 10px;
  color: #667085;
  font-size: 12px;
}

.instructor-profile-type {
  display: inline-flex;
  padding: 5px 10px;
  border-radius: 999px;
  color: #356ac3;
  background: #eef4ff;
  font-size: 11px;
  font-weight: 700;
}

.instructor-profile-meta {
  display: grid;
  gap: 0;
  margin: 20px 0 0;
  text-align: left;
}

.instructor-profile-meta > div {
  display: grid;
  grid-template-columns: minmax(92px, auto) minmax(0, 1fr);
  gap: 10px;
  padding: 10px 0;
  border-top: 1px solid #edf0f4;
}

.instructor-profile-meta dt,
.instructor-profile-meta dd {
  margin: 0;
  overflow-wrap: anywhere;
  font-size: 11px;
}

.instructor-profile-meta dt {
  color: #8a95a7;
}

.instructor-profile-meta dd {
  color: #3d485d;
  font-weight: 600;
  text-align: right;
}

.instructor-profile-meta .status-active {
  color: #2e9e65;
}

.instructor-profile-meta .status-passive {
  color: #d74c4c;
}

.instructor-profile-body {
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-width: 0;
}

.instructor-profile-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.instructor-profile-stat {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;
  padding: 15px;
  border: 1px solid #e1e6ee;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 6px 20px rgba(31, 43, 68, 0.05);
}

.instructor-profile-stat > i {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  border-radius: 10px;
  color: #356ac3;
  background: #eef4ff;
  font-size: 18px;
}

.instructor-profile-stat div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.instructor-profile-stat strong {
  color: #172033;
  font-size: 18px;
}

.instructor-profile-stat span {
  color: #8a95a7;
  font-size: 10px;
}

.instructor-profile-section {
  min-width: 0;
  padding: 20px;
  border: 1px solid #e1e6ee;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 6px 20px rgba(31, 43, 68, 0.05);
}

.instructor-profile-section h3 {
  margin: 0 0 10px;
  color: #253047;
  font-size: 15px;
}

.instructor-profile-section--about {
  min-height: 145px;
}

.instructor-profile-section--about p,
.instructor-profile-empty {
  margin: 0;
  color: #68758a;
  font-size: 13px;
  line-height: 1.7;
}

.instructor-profile-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
}

.instructor-profile-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.instructor-profile-tags span {
  padding: 6px 10px;
  border-radius: 999px;
  color: #3d4bc3;
  background: #eef1ff;
  font-size: 11px;
  font-weight: 600;
}

.instructor-profile-certificates {
  display: grid;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.instructor-profile-certificates li {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  color: #667085;
  font-size: 12px;
}

.instructor-profile-certificates i {
  color: #73c92d;
  font-size: 15px;
}

.instructor-profile-information {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.instructor-profile-information > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 5px;
  padding: 13px 14px;
  border-radius: 10px;
  background: #f7f9fc;
}

.instructor-profile-information span {
  color: #8a95a7;
  font-size: 10px;
}

.instructor-profile-information strong {
  overflow-wrap: anywhere;
  color: #3d485d;
  font-size: 12px;
  font-weight: 600;
}

.instructor-profile-linkedin {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  margin-top: 18px;
  color: #356ac3;
  font-size: 12px;
  font-weight: 600;
}

.instructor-profile-fade-enter-active,
.instructor-profile-fade-leave-active {
  transition: opacity 0.18s ease;
}

.instructor-profile-fade-enter,
.instructor-profile-fade-leave-to {
  opacity: 0;
}

@media (max-width: 640px) {
  .instructor-profile-overlay {
    padding: 10px;
  }

  .instructor-profile-dialog {
    max-height: calc(100vh - 20px);
  }

  .instructor-profile-scroll {
    max-height: calc(100vh - 32px);
    padding: 14px;
  }

  .instructor-profile-layout {
    grid-template-columns: 1fr;
    padding-top: 38px;
  }

  .instructor-profile-card {
    position: static;
  }

  .instructor-profile-stats,
  .instructor-profile-grid,
  .instructor-profile-information {
    grid-template-columns: 1fr;
  }
}
</style>
